import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "../lib/api";
import { Bot, Loader2, MessageCircle, RefreshCw, Search, Send, Wrench, Zap } from "lucide-react";

interface BotListItem {
  id: string;
  name: string;
  isActive: boolean | null;
  model: string | null;
  userId: string;
  userEmail: string | null;
}

interface BotDetail {
  bot: { id: string; name: string; isActive: boolean | null; model: string | null; userId: string };
  owner: { email: string | null; firstName: string | null; lastName: string | null } | null;
  integrations: Array<{ id: string; type: string; isActive: boolean | null }>;
  tools: Array<{ id: string; name: string; kind: string | null; isActive: boolean | null }>;
}

interface ToolCallDetail {
  name?: string;
  arguments?: unknown;
  result?: unknown;
  error?: string;
}

interface ChatMessage {
  id: string;
  role: string;
  content: string;
  metadata?: { toolCalls?: ToolCallDetail[]; toolContext?: string };
  createdAt?: string;
}

export default function BotTestPage() {
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [localMessages]);

  const botsQuery = useQuery<{ bots: BotListItem[] }>({
    queryKey: [`/api/admin/bot-test/bots?search=${encodeURIComponent(searchDebounced)}&limit=40`],
  });

  const botDetailQuery = useQuery<BotDetail>({
    queryKey: [`/api/admin/bot-test/bots/${selectedBotId}`],
    enabled: !!selectedBotId,
  });

  const sessionQuery = useQuery<{
    messages: ChatMessage[];
    bot: { name: string; model: string | null; isActive: boolean | null } | null;
  }>({
    queryKey: [`/api/admin/bot-test/sessions/${chatId}`],
    enabled: !!chatId,
  });

  useEffect(() => {
    if (sessionQuery.data?.messages) {
      setLocalMessages(sessionQuery.data.messages);
    }
  }, [sessionQuery.data?.messages]);

  const createSessionMutation = useMutation({
    mutationFn: async (botId: string) => {
      const res = await apiRequest("POST", `/api/admin/bot-test/bots/${botId}/sessions`, {});
      return res.json() as Promise<{ chatId: string }>;
    },
    onSuccess: (data) => {
      setChatId(data.chatId);
      setLocalMessages([]);
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/bot-test/sessions/${chatId}/messages`,
        { content },
      );
      return res.json() as Promise<{
        userMessage: ChatMessage;
        assistantMessage: ChatMessage;
        insufficientBalance?: boolean;
      }>;
    },
    onSuccess: (data) => {
      setLocalMessages((prev) => [...prev, data.userMessage, data.assistantMessage]);
      if (chatId) {
        queryClient.invalidateQueries({ queryKey: [`/api/admin/bot-test/sessions/${chatId}`] });
      }
    },
  });

  const handleSelectBot = (bot: BotListItem) => {
    setSelectedBotId(bot.id);
    setChatId(null);
    setLocalMessages([]);
    createSessionMutation.mutate(bot.id);
  };

  const handleNewSession = () => {
    if (!selectedBotId) return;
    setLocalMessages([]);
    createSessionMutation.mutate(selectedBotId);
  };

  const handleSend = () => {
    if (!chatId || !input.trim() || sendMutation.isPending) return;
    const text = input.trim();
    setInput("");
    sendMutation.mutate(text);
  };

  const lastAssistant = [...localMessages].reverse().find((m) => m.role === "assistant");
  const lastToolCalls = lastAssistant?.metadata?.toolCalls ?? [];

  return (
    <div className="space-y-4 h-[calc(100vh-3rem)] flex flex-col">
      <div>
        <h1 className="text-2xl font-bold">Тест клиентских ботов</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Песочница: сообщения идут в тот же AI-pipeline с инструментами и webhooks, что и в проде, но
          без Telegram, WhatsApp, виджета и других каналов. Баланс клиента не списывается.
        </p>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0">
        {/* Bot picker */}
        <div className="lg:col-span-3 flex flex-col border rounded-lg bg-card min-h-0">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Имя, id или email..."
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-md bg-background"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {botsQuery.isLoading && (
              <div className="text-sm text-muted-foreground p-2">Загрузка...</div>
            )}
            {botsQuery.data?.bots.map((bot) => (
              <button
                key={bot.id}
                type="button"
                onClick={() => handleSelectBot(bot)}
                className={`w-full text-left p-2 rounded-md text-sm transition-colors ${
                  selectedBotId === bot.id ? "bg-primary/10 border border-primary/30" : "hover:bg-muted"
                }`}
              >
                <div className="font-medium truncate">{bot.name}</div>
                <div className="text-xs text-muted-foreground truncate">{bot.userEmail}</div>
                <div className="text-xs text-muted-foreground font-mono truncate">{bot.id}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat */}
        <div className="lg:col-span-6 flex flex-col border rounded-lg bg-card min-h-0">
          <div className="p-3 border-b flex items-center justify-between gap-2">
            <div className="min-w-0">
              {botDetailQuery.data ? (
                <>
                  <div className="font-semibold truncate flex items-center gap-2">
                    <Bot className="w-4 h-4 shrink-0" />
                    {botDetailQuery.data.bot.name}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {botDetailQuery.data.owner?.email} · {botDetailQuery.data.bot.model}
                    {!botDetailQuery.data.bot.isActive && " · выключен"}
                  </div>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">Выберите бота слева</span>
              )}
            </div>
            {selectedBotId && (
              <button
                type="button"
                onClick={handleNewSession}
                disabled={createSessionMutation.isPending}
                className="shrink-0 flex items-center gap-1 text-xs px-2 py-1 border rounded hover:bg-muted"
              >
                <RefreshCw className={`w-3 h-3 ${createSessionMutation.isPending ? "animate-spin" : ""}`} />
                Новая сессия
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {!chatId && (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm">
                <MessageCircle className="w-10 h-10 mb-2 opacity-40" />
                Выберите бота для начала теста
              </div>
            )}
            {chatId && localMessages.length === 0 && !sendMutation.isPending && (
              <div className="text-center text-sm text-muted-foreground py-8">
                Сессия создана. Напишите сообщение как клиент.
              </div>
            )}
            {localMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground rounded-tr-md"
                      : "bg-muted rounded-tl-md"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {sendMutation.isPending && (
              <div className="flex gap-2 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Генерация ответа и вызов tools...
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 border-t flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSend())}
              placeholder={chatId ? "Сообщение клиента..." : "Сначала выберите бота"}
              disabled={!chatId || sendMutation.isPending}
              className="flex-1 px-3 py-2 text-sm border rounded-md bg-background disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!chatId || !input.trim() || sendMutation.isPending}
              className="p-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          {sendMutation.isError && (
            <p className="px-3 pb-2 text-xs text-red-600">
              {(sendMutation.error as Error).message}
            </p>
          )}
        </div>

        {/* Debug panel */}
        <div className="lg:col-span-3 flex flex-col border rounded-lg bg-card min-h-0 overflow-hidden">
          <div className="p-3 border-b font-medium text-sm">Интеграции и tools</div>
          <div className="flex-1 overflow-y-auto p-3 space-y-4 text-sm">
            {botDetailQuery.data && (
              <>
                <div>
                  <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                    <Zap className="w-3 h-3" /> Интеграции
                  </div>
                  {botDetailQuery.data.integrations.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Нет</p>
                  ) : (
                    <ul className="space-y-1">
                      {botDetailQuery.data.integrations.map((i) => (
                        <li key={i.id} className="text-xs font-mono">
                          {i.type}
                          {!i.isActive && " (off)"}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                    <Wrench className="w-3 h-3" /> Tools
                  </div>
                  {botDetailQuery.data.tools.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Нет</p>
                  ) : (
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {botDetailQuery.data.tools.map((t) => (
                        <li key={t.id} className="text-xs truncate" title={t.name}>
                          {t.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Tool calls (последний ответ)
              </div>
              {lastToolCalls.length === 0 ? (
                <p className="text-xs text-muted-foreground">Пока нет</p>
              ) : (
                <div className="space-y-2">
                  {lastToolCalls.map((tc, idx) => (
                    <pre
                      key={idx}
                      className="text-[10px] bg-muted p-2 rounded overflow-x-auto max-h-40"
                    >
                      {JSON.stringify(tc, null, 2)}
                    </pre>
                  ))}
                </div>
              )}
            </div>

            {lastAssistant?.metadata?.toolContext && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">toolContext</div>
                <pre className="text-[10px] bg-muted p-2 rounded overflow-x-auto max-h-24">
                  {lastAssistant.metadata.toolContext}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
